import { BasePatientDetailsFormFields, CustomerType, PatientDetailsFormFields, SignatureFormFields, Title } from 'common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { merge, startWith, debounceTime, map, distinctUntilChanged, takeUntil, Subject } from 'rxjs';
import { CustomerSignaturePagesComponent } from './signing-pages/customer-signature-pages.component';
import { PatientSignaturePagesComponent } from './signing-pages/patient-signature-pages.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { toTitle } from '../../../../utils/static-functions';
import { NgClass } from '@angular/common';


@Component({
  selector: 'lib-signing-accordion',
  standalone: true,
  imports: [NgClass, MatExpansionModule, MatIconModule, CustomerSignaturePagesComponent, PatientSignaturePagesComponent],
  templateUrl: './signing-accordion.component.html',
  styleUrl: './signing-accordion.component.scss',
  animations: [
    trigger(
      'fadeInOut',
      [
        transition(
          ':enter', [
          style({ opacity: 0 }),
          animate('0.3s ease-out',
            style({ opacity: 1 }))
        ]),
        transition(
          ':leave', [
          style({ opacity: 1 }),
          animate('0.3s ease-in',
            style({ opacity: 0 }))
        ])
      ]
    )
  ]
})
export class SigningAccordionComponent implements OnInit, OnDestroy, OnChanges {

  //#region Variables
  // Variables for this component that are within scope of "this" component.

  @Input({ required: true })
  public isCustomerAPatient!: boolean;

  @Input({ required: true })
  public isReadMode!: boolean;

  @Input({ required: true })
  public form!: RelevantFormGroup;

  @Input()
  public invokeMarkDirty = false;

  @Output()
  public validityChanged = new EventEmitter<boolean>();

  public areAllFormsValid = false;

  private destroy$ = new Subject<void>();

  //#endregion
  //#region Instantiation
  // Setup\fetches\initialization.

  public ngOnInit(): void {
    // Monitor each form for changes.
    merge(
      this.patientsArray.valueChanges,
      this.customerSignatureForm.valueChanges,
    )
      .pipe(
        startWith(null), // Start with the current value.
        debounceTime(300), // Prevent over-lapping emissions from hindering performance.
        map(() => { // Map to boolean type.

          // Since validation is done manually "on the fly", we need to check if the form is valid. 
          const isCustomerValid = this.areCustomerFieldsComplete;
          const arePatientsValid = this.patientsWhoMustSign.every(p => this.arePatientFieldsComplete(p));

          return isCustomerValid && arePatientsValid;
        }),
        distinctUntilChanged(), // Ignore non-unique value changes.
        takeUntil(this.destroy$)
      )
      .subscribe(isValid => {
        this.validityChanged.emit(isValid);
        this.areAllFormsValid = isValid;
      });
  }

  //#endregion
  //#region User Interactions
  // Callbacks from html, anything the user can "interact" with from the view.


  //#endregion
  //#region Events and Domain Functions
  // $watches/$on events, functions which don't fall into any of the other regions,
  // including any function that has to do domain functionality.

  public markCustomerFormsTouched() {
    const controls = this.getCustomerSignatureControls();

    Object.values(controls).forEach(control => {
      control.markAsTouched();
      control.markAsDirty();
    });
  }

  public markPatientFormTouched(patientForm: any) {
    const pair = this.getPatientControlPair(patientForm as FormGroup<PatientDetailsFormFields>);
    pair.controlId?.markAsTouched();
    pair.controlId?.markAsDirty();

    pair.controlResource?.markAsTouched();
    pair.controlResource?.markAsDirty();
  }

  //#endregion
  //#region Data Access & Subscriptions
  // API interactions, NGRX selects, etc.

  //#endregion
  //#region Setters, Updaters and Preloaders
  // Setter functions, and methods called from the Instantiation region.

  //#endregion
  //#region Getters and Filters
  // Getter functions and filter methods/functions.

  /**
   * Returns the customer's signature form.
   *
   * @readonly
   */
  public get customerSignatureForm(): FormGroup<SignatureFormFields> {
    return this.form.controls['signatures'] as FormGroup<SignatureFormFields>;
  }

  /**
   * Returns the patients from the form.
   *
   * @readonly
   */
  public get patientsArray() {
    return this.form.controls['patients'] as FormArray<FormControl<PatientDetailsFormFields | null>>;
  }

  get patientsArrayWithoutCustomer() {
    const allPatients = this.form.controls["patients"];
    const controls = allPatients.controls;

    return controls.filter((c, i) => i !== 0) as FormControl<PatientDetailsFormFields | null>[];
  }

  get patientsArrayWithoutCustomerOrAuthorizer() {
    const allPatients = this.form.controls["patients"];
    const controls = allPatients.controls;

    return controls.filter((c, i) => i !== 0 && (c.value as any)?.customerType !== CustomerType.Authorizer) as FormControl<PatientDetailsFormFields | null>[];
  }

  get patientsWhoMustSign() {
    const allPatients = this.form.controls["patients"];
    const controls = allPatients.controls;

    return controls
      .filter((c, i) =>
        // Remove the first patient (the customer).
        i !== 0
        // Remove Authorizer as they are not required to sign.
        && (c.value as any)?.customerType !== CustomerType.Authorizer
      ) as FormControl<PatientDetailsFormFields | null>[];
  }

  /**
   * Returns all the customer's signature controls.
   * The controls are:
   * - signatureQuotationId
   * - signatureQuotation
   * - signatureTermsId
   * - signatureTerms
   * - signatureMandateId
   * - signatureMandate
   * - agreeMandate
   * - agreeMarketing
   * - agreeTermsSpecsfin
   * - notUnderReview
   *
   * @return {*} Each of the customer's signature controls.
   */
  private getCustomerSignatureControls() {
    const signatureQuotationId = this.customerSignatureForm.controls['signatureQuotationId'];
    const signatureQuotation = this.customerSignatureForm.controls['signatureQuotation'];
    const signatureTermsId = this.customerSignatureForm.controls['signatureTermsId'];
    const signatureTerms = this.customerSignatureForm.controls['signatureTerms'];
    const signatureMandateId = this.customerSignatureForm.controls['signatureMandateId'];
    const signatureMandate = this.customerSignatureForm.controls['signatureMandate'];
    const agreeMandate = this.customerSignatureForm.controls['agreeMandate'];
    const agreeMarketing = this.customerSignatureForm.controls['agreeMarketing'];
    const agreeTermsSpecsfin = this.customerSignatureForm.controls['agreeTermsSpecsfin'];
    const notUnderReview = this.customerSignatureForm.controls['notUnderReview'];


    return { signatureQuotationId, signatureQuotation, signatureTermsId, signatureTerms, signatureMandateId, signatureMandate, agreeMandate, agreeMarketing, agreeTermsSpecsfin, notUnderReview }
  }

  /**
   * Returns the patient's signature form controls.
   * 
   * @param {FormGroup<PatientDetailsFormFields>} patientForm The patient form.
   * @return {{ controlId: FormControl<number | null>, controlResource: FormControl<Resource | null> }} The patient's signature form controls.
   */
  private getPatientControlPair(patientForm: FormGroup<PatientDetailsFormFields>) {
    const controlId = patientForm.controls['signaturePatientId'];
    const controlResource = patientForm.controls['signaturePatient'];

    return { controlId, controlResource }
  }

  //#endregion
  //#region Change Detection
  // Deep config comparisons, change log generation, hasChangesFn, etc.


  public ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes['invokeMarkDirty']) {
      if (this.invokeMarkDirty === true) {
        this.markCustomerFormsTouched();
        this.patientsArray.controls.forEach(patientForm => this.markPatientFormTouched(patientForm));
      }
    }
  }

  //#endregion    
  //#region Parsing and Validation
  // Transformation/mapping methods
  // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

  public toTitle(title?: Title | null) {
    return toTitle(title)
  }

  /**
   * Returns true if the customer fields are complete.
   *
   * @readonly
   * @type {boolean} True if the customer fields are complete.
   */
  public get areCustomerFieldsComplete(): boolean {
    const controls = this.getCustomerSignatureControls();
    const areAllComplete = Object.values(controls).every(control => {

      if ((control?.value || null) === null) return false;
      if ((control.value || null) instanceof Boolean) {
        return control.value === false;
      }

      return true;
    });
    return areAllComplete;
  }

  /**
   * Returns true if the customer fields are touched.
   *
   * @readonly
   * @type {boolean} True if the customer fields are touched.
   */
  public get hasTouchedCustomerFields(): boolean {
    const controls = this.getCustomerSignatureControls();

    const areAnyTouched = Object.values(controls).some(control => control?.touched || false);
    return areAnyTouched;
  }

  /**
   * Returns true if the customer fields are dirty.
   *
   * @readonly
   * @type {boolean} True if the customer fields are dirty.
   */
  public get areCustomerFieldsDirty(): boolean {
    const controls = this.getCustomerSignatureControls();

    const areAnyDirty = Object.values(controls).some(control => control?.dirty || false);
    return areAnyDirty;
  }

  /**
   * Returns true if the customer fields are complete.
   * @param {FormGroup<PatientDetailsFormFields>} patientForm The patient form. "Any" cast is because angular gets confused between FormControls and FormGroups within FormArrays. 
   * @returns {boolean} True if the patient fields are complete.
   */
  public arePatientFieldsComplete(patientForm: any): boolean {
    if (!patientForm || !patientForm.controls) return false;

    const pair = this.getPatientControlPair(patientForm as FormGroup<PatientDetailsFormFields>);
    return pair?.controlId?.value !== null && pair?.controlResource?.value !== null;
  }

  /**
   * Returns true if the patient fields are touched.
   *
   * @param {FormGroup<PatientDetailsFormFields>} patientForm The patient form. "Any" cast is because angular gets confused between FormControls and FormGroups within FormArrays. 
   * @return {boolean} True if the patient fields are touched.
   */
  public hasTouchedPatientFields(patientForm: any): boolean {
    if (!patientForm || !patientForm.controls) return false;

    const pair = this.getPatientControlPair(patientForm as FormGroup<PatientDetailsFormFields>);
    return pair.controlId?.touched || pair.controlResource?.touched;
  }

  /**
   * Returns true if the patient fields are dirty.
   *
   * @param {FormGroup<PatientDetailsFormFields>} patientForm The patient form. "Any" cast is because angular gets confused between FormControls and FormGroups within FormArrays.
   * @return {boolean} True if the patient fields are dirty.
   */
  public arePatientFieldsDirty(patientForm: any): boolean {
    if (!patientForm || !patientForm.controls) return false;

    const pair = this.getPatientControlPair(patientForm as FormGroup<PatientDetailsFormFields>);
    return pair.controlId?.dirty || pair.controlResource?.dirty;
  }

  //#endregion
  //#region Response Handlers
  // API/NGRX callback handlers.

  //#endregion
  //#region Navigation
  // Confirmation close dialogs, router guards, etc.

  //#endregion
  //#region Post Instantiation
  // Post-init fetch requests, used in such as landing page, after view init, etc.

  //#endregion


  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}

/**
 * The relevant fields for the signing process.
 * ALL fields on the form from the capture dialog are present as this is the master form.
 * The reduction of fields is to narrow interest and help prevent errors.
 */
interface RelevantFormGroup extends FormGroup<any> {
  controls: {
    /**
     * Array of each patient's details.
     */
    patients: FormArray<FormControl<BasePatientDetailsFormFields | null>>,

    /**
     * The customer's information.
     */
    customerInformation: FormGroup<PatientDetailsFormFields>

    /**
     * The signatures form.
     * Contains the customer's signature and the patient's signatures.
     */
    signatures: FormGroup<SignatureFormFields>

    /**
     * The Vendor's ID.
     */
    vendor: FormControl<number | null>
  }
}